import React, { useEffect, useState } from 'react';
import GlobalHeader from './GlobalHeader';
import certificatesData from '../data/certificates.json';
import theme from '../config/theme';

interface CertificateDetailProps {
    certId: string;
    user: any;
    onSignOut: () => void;
    onSignIn: () => void;
    onBack: () => void;
}

const CertificateDetail: React.FC<CertificateDetailProps> = ({ certId, user, onSignOut, onSignIn, onBack }) => {
    const [certificate, setCertificate] = useState<any | null>(null);

    useEffect(() => {
        const found = (certificatesData as any[]).find(c => c.certId === certId);
        setCertificate(found || null);
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, [certId]);

    if (!certificate) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: theme.colors.bg
            }}>
                Loading...
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${theme.colors.bg} 0%, ${theme.colors.bgAlt} 100%)`,
            paddingTop: '80px'
        }}>
            <GlobalHeader
                user={user}
                onSignOut={onSignOut}
                onSignIn={onSignIn}
                activeTab="certified-testers"
            />

            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '40px 20px',
            }}>
                {/* Back Button */}
                <button
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        color: '#64748b',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginBottom: '32px',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.5)';
                        e.currentTarget.style.color = '#1e293b';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#64748b';
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Certified Testers
                </button>

                <style>
                    {`
                    .certificate-container {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }
                    .cert-image-container {
                        width: 100%;
                        padding: 24px;
                        border-bottom: 1px solid #e2e8f0;
                        box-sizing: border-box;
                    }
                    .cert-details-container {
                        width: 100%;
                        padding: 32px;
                        box-sizing: border-box;
                    }

                    @media (min-width: 768px) {
                        .certificate-container {
                            flex-direction: row;
                            align-items: stretch;
                        }
                        .cert-image-container {
                            width: 60%;
                            padding: 40px;
                            border-bottom: none;
                            border-right: 1px solid #e2e8f0;
                        }
                        .cert-details-container {
                            width: 40%;
                            padding: 48px;
                        }
                    }
                    `}
                </style>
                <div className="certificate-container" style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                }}>
                    {/* Left Column: Image */}
                    <div className="cert-image-container" style={{
                        backgroundColor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <img
                            src={certificate.image?.large?.url || certificate.image?.medium?.url}
                            alt="Certificate"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '12px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                            }}
                        />
                    </div>

                    {/* Right Column: Details */}
                    <div className="cert-details-container" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        backgroundColor: '#ffffff'
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#ecfdf5',
                            color: '#059669',
                            padding: '8px 16px',
                            borderRadius: '50px',
                            fontSize: '14px',
                            fontWeight: '700',
                            marginBottom: '24px',
                            border: '1px solid #d1fae5'
                        }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                            </svg>
                            Official Certificate
                        </div>

                        <h1 style={{
                            fontSize: '32px',
                            fontWeight: '800',
                            color: '#1e293b',
                            marginBottom: '8px',
                            lineHeight: '1.2'
                        }}>
                            {certificate.studentFullName}
                        </h1>

                        <p style={{
                            fontSize: '18px',
                            color: '#64748b',
                            marginBottom: '32px',
                            fontWeight: '500'
                        }}>
                            has successfully completed the course
                            <br />
                            <span style={{ color: '#4f46e5', fontWeight: '700' }}>{certificate.coureName}</span>
                        </p>

                        <div style={{
                            display: 'grid',
                            gap: '24px',
                            borderTop: '1px solid #e2e8f0',
                            paddingTop: '32px'
                        }}>
                            <div>
                                <div style={{ fontSize: '13px', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '700', letterSpacing: '0.05em', marginBottom: '4px' }}>
                                    Certificate ID
                                </div>
                                <div style={{ fontSize: '16px', color: '#334155', fontWeight: '600', fontFamily: 'monospace' }}>
                                    {certificate.certId}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: '13px', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '700', letterSpacing: '0.05em', marginBottom: '4px' }}>
                                    Date of Issue
                                </div>
                                <div style={{ fontSize: '16px', color: '#334155', fontWeight: '600' }}>
                                    {new Date(certificate.dateIssue.$date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: '13px', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '700', letterSpacing: '0.05em', marginBottom: '4px' }}>
                                    Issued By
                                </div>
                                <div style={{ fontSize: '16px', color: '#334155', fontWeight: '600' }}>
                                    SDET PRO
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default CertificateDetail;
